import { memo, useState, useCallback, useRef } from "react";
import { CloudIcons } from "./CloudIcons";

// Service detail data for tooltips
const SERVICE_DETAILS = {
  route53: {
    name: "Amazon Route 53",
    category: "DNS & Routing",
    pricing: "$0.50/hosted zone + $0.40/M queries",
    specs: "100% SLA, latency-based routing, health checks",
    tip: "Use alias records for AWS resources to avoid query charges",
  },
  cloudfront: {
    name: "Amazon CloudFront",
    category: "CDN & Edge",
    pricing: "$0.085/GB first 10TB",
    specs: "450+ PoPs, HTTP/3, real-time logs",
    tip: "Enable Origin Shield to reduce origin load by up to 50%",
  },
  waf: {
    name: "AWS WAF",
    category: "Security",
    pricing: "$5/web ACL + $1/rule + $0.60/M requests",
    specs: "Bot control, rate limiting, managed rule groups",
    tip: "Start with AWS Managed Rules before writing custom rules",
  },
  elb: {
    name: "Application Load Balancer",
    category: "Networking",
    pricing: "$0.0225/hr + $0.008/LCU-hr",
    specs: "Layer 7, path-based routing, gRPC support",
    tip: "Use target group slow start to avoid overwhelming new instances",
  },
  iam: {
    name: "Amazon Cognito",
    category: "Identity & Auth",
    pricing: "Free tier: 50K MAU, then $0.0055/MAU",
    specs: "OAuth 2.0, SAML, MFA, hosted UI",
    tip: "Use Cognito groups to map directly to IAM roles for fine-grained access",
  },
  ecs: {
    name: "Amazon ECS (Fargate)",
    category: "Container Compute",
    pricing: "$0.04048/vCPU-hr + $0.004445/GB-hr",
    specs: "Serverless containers, auto-scaling, ARM support",
    tip: "Use Fargate Spot for fault-tolerant workloads — up to 70% savings",
  },
  lambda: {
    name: "AWS Lambda",
    category: "Serverless Compute",
    pricing: "$0.20/M requests + $0.0000166667/GB-sec",
    specs: "15 min timeout, 10GB memory, SnapStart",
    tip: "Use Provisioned Concurrency for latency-sensitive APIs",
  },
  secretsmanager: {
    name: "AWS Secrets Manager",
    category: "Security",
    pricing: "$0.40/secret/month + $0.05/10K API calls",
    specs: "Auto-rotation, cross-account access, audit via CloudTrail",
    tip: "Enable automatic rotation for RDS credentials every 30 days",
  },
  rds: {
    name: "Amazon RDS (PostgreSQL)",
    category: "Relational Database",
    pricing: "db.r6g.large: ~$0.26/hr (Multi-AZ)",
    specs: "Multi-AZ, read replicas, automated backups",
    tip: "Use RDS Proxy to pool connections and reduce failover time by 66%",
  },
  elasticache: {
    name: "Amazon ElastiCache (Redis)",
    category: "In-Memory Cache",
    pricing: "cache.r6g.large: ~$0.166/hr",
    specs: "Sub-millisecond latency, cluster mode, data tiering",
    tip: "Use r6gd instances with data tiering for 60% cost savings on large datasets",
  },
  s3: {
    name: "Amazon S3",
    category: "Object Storage",
    pricing: "$0.023/GB (Standard), $0.0125/GB (IA)",
    specs: "11 9s durability, versioning, lifecycle policies",
    tip: "Set up S3 Intelligent-Tiering for automatic cost optimization",
  },
  cloudwatch: {
    name: "Amazon CloudWatch",
    category: "Monitoring & Observability",
    pricing: "$0.30/metric, $0.50/GB logs ingested",
    specs: "Custom metrics, alarms, dashboards, Container Insights",
    tip: "Use metric math to create composite alarms and reduce alert noise",
  },
};

// Service node with hover interaction
const SvcNode = ({ x, y, service, label, size = 48, onHover, onLeave, serviceId }) => {
  const icon = CloudIcons.aws?.[service] || CloudIcons.generic?.[service] || CloudIcons.generic.server;
  return (
    <g
      transform={`translate(${x}, ${y})`}
      onMouseEnter={(e) => onHover(serviceId || service, e)}
      onMouseLeave={onLeave}
      style={{ cursor: "pointer" }}
      data-testid={`svc-node-${serviceId || service}`}
    >
      {/* Invisible hit area for better hover detection */}
      <rect width={size} height={size + 16} fill="transparent" />
      <foreignObject width={size} height={size} x={0} y={0}>
        <div style={{ width: size, height: size }} xmlns="http://www.w3.org/1999/xhtml">
          {icon}
        </div>
      </foreignObject>
      <text
        x={size / 2}
        y={size + 12}
        textAnchor="middle"
        fill="#d4d4d8"
        fontSize={8.5}
        fontWeight={600}
        fontFamily="'JetBrains Mono', monospace"
      >
        {label}
      </text>
    </g>
  );
};

// Connection arrow
const Connector = ({ x1, y1, x2, y2, dashed, label }) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#00f0ff" strokeWidth={1.4} strokeDasharray={dashed ? "5 3" : "none"} opacity={0.5} markerEnd="url(#arr)" />
      {label && (
        <>
          <rect x={mx - label.length * 2.8 - 3} y={my - 9} width={label.length * 5.5 + 6} height={12} rx={2} fill="#08080c" />
          <text x={mx} y={my - 1} textAnchor="middle" fill="#52525b" fontSize={7} fontFamily="'JetBrains Mono', monospace">{label}</text>
        </>
      )}
    </g>
  );
};

// Boundary box
const Boundary = ({ x, y, w, h, label, color }) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx={8} fill="transparent" stroke={color} strokeWidth={1.6} strokeDasharray="8 4" opacity={0.35} />
    <rect x={x + 8} y={y - 9} width={label.length * 5.8 + 14} height={18} rx={3} fill="#08080c" stroke={color} strokeWidth={1} opacity={0.8} />
    <text x={x + 15} y={y + 3} fill={color} fontSize={8.5} fontWeight={700} fontFamily="'JetBrains Mono', monospace" opacity={0.9}>{label}</text>
  </g>
);

// Category color map
const CAT_COLORS = {
  "DNS & Routing": "#8C4FFF",
  "CDN & Edge": "#8C4FFF",
  Networking: "#8C4FFF",
  "Identity & Auth": "#DD344C",
  Security: "#DD344C",
  "Container Compute": "#ED7100",
  "Serverless Compute": "#ED7100",
  "Relational Database": "#3B48CC",
  "In-Memory Cache": "#3B48CC",
  "Object Storage": "#569A31",
  "Monitoring & Observability": "#E7157B",
};

// Tooltip component
const Tooltip = ({ data, position }) => {
  if (!data) return null;
  const color = CAT_COLORS[data.category] || "#00f0ff";

  return (
    <div
      className="absolute z-50 pointer-events-none"
      style={{ left: position.x, top: position.y, transform: "translate(-50%, -105%)" }}
      data-testid="service-tooltip"
    >
      <div className="bg-zinc-900/95 backdrop-blur-md border border-zinc-700/80 rounded-lg shadow-xl shadow-black/40 p-3 w-[260px]">
        {/* Header */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
          <span className="text-white text-xs font-bold font-mono">{data.name}</span>
        </div>
        <div className="text-[10px] font-mono mb-2.5" style={{ color }}>{data.category}</div>

        {/* Details */}
        <div className="space-y-1.5 text-[10px] font-mono">
          <div className="flex gap-2">
            <span className="text-zinc-500 shrink-0 w-[42px]">Cost</span>
            <span className="text-zinc-300">{data.pricing}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-zinc-500 shrink-0 w-[42px]">Specs</span>
            <span className="text-zinc-300">{data.specs}</span>
          </div>
        </div>

        {/* Best Practice tip */}
        <div className="mt-2.5 pt-2 border-t border-zinc-700/60">
          <div className="flex gap-1.5 items-start">
            <span className="text-[#00f0ff] text-[10px] font-mono font-bold shrink-0">TIP</span>
            <span className="text-zinc-400 text-[10px] font-mono leading-relaxed">{data.tip}</span>
          </div>
        </div>

        {/* Arrow */}
        <div className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 w-3 h-3 bg-zinc-900/95 border-r border-b border-zinc-700/80 rotate-45" />
      </div>
    </div>
  );
};

export const AwsArchitectureShowcase = memo(() => {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const W = 700, H = 470;
  const S = 44;
  const half = S / 2;

  const t1 = 85, t2 = 240, t3 = 375;
  const x1 = 80, x2 = 170, x3 = 260, x4 = 365, x5 = 470;

  const handleHover = useCallback((serviceId, e) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const svgEl = container.querySelector("svg");
    if (!svgEl) return;

    // Get mouse position relative to the container
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    setHovered(SERVICE_DETAILS[serviceId] || null);
    setTooltipPos({ x: mouseX, y: mouseY - 10 });
  }, []);

  const handleLeave = useCallback(() => setHovered(null), []);

  return (
    <div ref={containerRef} className="relative bg-[#08080c] border border-zinc-700/50 rounded-xl overflow-visible shadow-2xl" data-testid="aws-architecture-showcase">
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-zinc-900/60 border-b border-zinc-800/80">
        <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]"></div>
        <span className="ml-3 text-[11px] text-zinc-500 font-mono tracking-wide">architecture.aws — 3-Tier E-Commerce</span>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="px-2 py-0.5 bg-[#FF9900]/10 border border-[#FF9900]/30 rounded text-[9px] text-[#FF9900] font-bold font-mono">AWS</span>
          <span className="px-2 py-0.5 bg-green-500/10 border border-green-500/30 rounded text-[9px] text-green-400 font-mono">Multi-AZ</span>
          <span className="px-2 py-0.5 bg-[#00f0ff]/10 border border-[#00f0ff]/30 rounded text-[9px] text-[#00f0ff] font-mono">us-east-1</span>
        </div>
      </div>

      {/* Tooltip */}
      <Tooltip data={hovered} position={tooltipPos} />

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ minHeight: "300px" }}>
        <defs>
          <marker id="arr" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
            <polygon points="0 0, 7 2.5, 0 5" fill="#00f0ff" opacity="0.5" />
          </marker>
          <pattern id="gridDot" width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="0.4" fill="#ffffff" opacity="0.04" />
          </pattern>
        </defs>

        <rect width={W} height={H} fill="url(#gridDot)" />

        {/* === BOUNDARIES === */}
        <Boundary x={45} y={20} w={W - 90} h={H - 40} label="VPC 10.0.0.0/16" color="#8C4FFF" />
        <Boundary x={60} y={48} w={W - 120} h={105} label="Public Subnet (10.0.1.0/24)" color="#569A31" />
        <Boundary x={60} y={185} w={W - 120} h={250} label="Private Subnet (10.0.2.0/24)" color="#527FFF" />

        {/* Users icon */}
        <g transform="translate(14, 70)">
          <circle cx={14} cy={8} r={6} fill="none" stroke="#52525b" strokeWidth={1.2} />
          <path d="M14 16c-6 0-10 2.5-10 5v2h20v-2c0-2.5-4-5-10-5z" fill="none" stroke="#52525b" strokeWidth={1.2} />
          <text x={14} y={34} textAnchor="middle" fill="#52525b" fontSize={7.5} fontWeight={600} fontFamily="'JetBrains Mono', monospace">Users</text>
        </g>

        {/* === EDGE TIER === */}
        <SvcNode x={x1} y={t1 - half} service="route53" label="Route 53" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x2} y={t1 - half} service="cloudfront" label="CloudFront" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x3} y={t1 - half} service="waf" label="AWS WAF" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x4} y={t1 - half} service="elb" label="ALB" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x5} y={t1 - half} service="iam" label="Cognito" size={S} onHover={handleHover} onLeave={handleLeave} serviceId="iam" />

        {/* === APPLICATION TIER === */}
        <SvcNode x={x2} y={t2 - half} service="ecs" label="ECS (API)" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x3 + 10} y={t2 - half} service="ecs" label="ECS (Worker)" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x4 + 15} y={t2 - half} service="lambda" label="Lambda" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x5 + 25} y={t2 - half} service="secretsmanager" label="Secrets" size={S} onHover={handleHover} onLeave={handleLeave} />

        {/* === DATA TIER === */}
        <SvcNode x={x1} y={t3 - half} service="rds" label="RDS Primary" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x2 + 15} y={t3 - half} service="rds" label="RDS Replica" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x3 + 30} y={t3 - half} service="elasticache" label="ElastiCache" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x4 + 40} y={t3 - half} service="s3" label="S3 Bucket" size={S} onHover={handleHover} onLeave={handleLeave} />
        <SvcNode x={x5 + 35} y={t3 - half} service="cloudwatch" label="CloudWatch" size={S} onHover={handleHover} onLeave={handleLeave} />

        {/* === CONNECTIONS === */}
        <Connector x1={42} y1={t1} x2={x1} y2={t1} />
        <Connector x1={x1 + S} y1={t1} x2={x2} y2={t1} label="DNS" />
        <Connector x1={x2 + S} y1={t1} x2={x3} y2={t1} />
        <Connector x1={x3 + S} y1={t1} x2={x4} y2={t1} label="HTTPS" />
        <Connector x1={x4 + S} y1={t1} x2={x5} y2={t1} dashed label="Auth" />

        <Connector x1={x4 + half} y1={t1 + half} x2={x2 + half} y2={t2 - half} label=":443" />
        <Connector x1={x4 + half + 5} y1={t1 + half} x2={x3 + half + 10} y2={t2 - half} />
        <Connector x1={x4 + half + 10} y1={t1 + half} x2={x4 + half + 15} y2={t2 - half} dashed label="Events" />

        <Connector x1={x2 + half - 5} y1={t2 + half} x2={x1 + half} y2={t3 - half} label="SQL" />
        <Connector x1={x2 + half + 5} y1={t2 + half} x2={x3 + half + 30} y2={t3 - half} dashed label="Cache" />
        <Connector x1={x3 + half + 10} y1={t2 + half} x2={x2 + half + 15} y2={t3 - half} />

        <Connector x1={x1 + S} y1={t3} x2={x2 + 15} y2={t3} dashed label="Sync" />
        <Connector x1={x4 + half + 15} y1={t2 + half} x2={x4 + half + 40} y2={t3 - half} label="Put" />
        <Connector x1={x4 + S + 15} y1={t2} x2={x5 + 25} y2={t2} dashed />
        <Connector x1={x5 + half + 35} y1={t3 - half} x2={x5 + half + 25} y2={t2 + half} dashed />

        {/* AZ LABELS */}
        <g opacity={0.5}>
          <rect x={65} y={H - 25} width={250} height={16} rx={3} fill="#FF990008" stroke="#FF9900" strokeWidth={0.8} />
          <text x={190} y={H - 14} textAnchor="middle" fill="#FF9900" fontSize={7.5} fontWeight={600} fontFamily="'JetBrains Mono', monospace">Availability Zone A (us-east-1a)</text>
          <rect x={335} y={H - 25} width={250} height={16} rx={3} fill="#FF990008" stroke="#FF9900" strokeWidth={0.8} />
          <text x={460} y={H - 14} textAnchor="middle" fill="#FF9900" fontSize={7.5} fontWeight={600} fontFamily="'JetBrains Mono', monospace">Availability Zone B (us-east-1b)</text>
        </g>

        {/* Cost badge */}
        <g transform={`translate(${W - 108}, 28)`}>
          <rect width={90} height={18} rx={4} fill="#569A3120" stroke="#569A31" strokeWidth={0.8} />
          <text x={45} y={12.5} textAnchor="middle" fill="#569A31" fontSize={8} fontWeight={700} fontFamily="'JetBrains Mono', monospace">~$4,200/mo</text>
        </g>

        {/* Legend */}
        <g transform="translate(66, 438)" opacity={0.6}>
          {[
            { color: "#8C4FFF", label: "Networking" },
            { color: "#ED7100", label: "Compute" },
            { color: "#3B48CC", label: "Database" },
            { color: "#569A31", label: "Storage" },
            { color: "#DD344C", label: "Security" },
            { color: "#E7157B", label: "Monitoring" },
          ].map((item, i) => (
            <g key={item.label} transform={`translate(${i * 88}, 0)`}>
              <rect width={8} height={8} rx={2} fill={item.color} />
              <text x={12} y={8} fill="#71717a" fontSize={7} fontFamily="'JetBrains Mono', monospace">{item.label}</text>
            </g>
          ))}
        </g>
      </svg>

      {/* Hover hint */}
      {!hovered && (
        <div className="absolute bottom-2 right-3 text-[9px] text-zinc-600 font-mono opacity-60">
          Hover over services for details
        </div>
      )}
    </div>
  );
});

AwsArchitectureShowcase.displayName = "AwsArchitectureShowcase";
